import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import AnimatedNumber from "../../common/components/AnimatedNumber";
import ChartRenderer from "../../common/components/ChartRenderer";
import { formatChartLabels } from "../../common/config/chartConfig";

const EarningsCard = ({ analytics }) => {
  const { t } = useTranslation("analytics");
  const earnings = analytics?.user_earnings;
  const [showAssetBreakdown, setShowAssetBreakdown] = useState(false);
  const [showCurrencyBreakdown, setShowCurrencyBreakdown] = useState(false);
  const [showTrend, setShowTrend] = useState(false);

  if (!earnings?.has_expenses) {
    return null;
  }

  const totalEarnings = earnings?.total_earnings || 0;
  const totalPaid = earnings?.total_paid || 0;
  const remaining = earnings?.remaining || 0;
  const assetBreakdown = earnings?.asset_breakdown || [];
  const currencyBreakdown = earnings?.currency_breakdown || [];
  const timeStats = analytics?.time_stats || [];
  const granularity = analytics?.granularity || "monthly";

  const trendLabels = formatChartLabels(timeStats, granularity);
  const trendData = timeStats.map(
    (s) => (s.royalty_revenue || 0) + (s.impression_revenue || 0),
  );

  return (
    <Box sx={{ mt: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? "rgb(229, 246, 253)"
              : "rgb(1, 67, 97)",
        }}
      >
        <Typography variant="h3" sx={{ mb: 3 }}>
          {t("earningsCard.title")}
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>{t("earningsCard.totalEarned")}</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              <AnimatedNumber
                section="statsCards"
                value={totalEarnings}
                isCurrency
              />
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>{t("earningsCard.alreadyPaid")}</Typography>
            <Typography sx={{ fontWeight: 600 }}>
              <AnimatedNumber
                section="statsCards"
                value={totalPaid}
                isCurrency
              />
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography>{t("earningsCard.remainingToBePaid")}</Typography>
            <Typography color="primary" sx={{ fontWeight: 600 }}>
              <AnimatedNumber
                section="statsCards"
                value={remaining}
                isCurrency
              />
            </Typography>
          </Box>
        </Box>

        {/* Per-Asset Breakdown */}
        {assetBreakdown.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowAssetBreakdown((v) => !v)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("earningsCard.assetBreakdown")}
              </Typography>
              <IconButton size="small">
                {showAssetBreakdown ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </IconButton>
            </Box>
            <Collapse in={showAssetBreakdown}>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("earningsCard.columns.title")}</TableCell>
                    <TableCell align="right">
                      {t("earningsCard.columns.earned")}
                    </TableCell>
                    <TableCell align="right">
                      {t("earningsCard.columns.paid")}
                    </TableCell>
                    <TableCell align="right">
                      {t("earningsCard.columns.remaining")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assetBreakdown.map((a) => (
                    <TableRow key={a.asset_id}>
                      <TableCell>{a.asset_title}</TableCell>
                      <TableCell align="right">
                        ${a.total_earnings?.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ${a.total_paid?.toFixed(2)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: "primary.main", fontWeight: 600 }}
                      >
                        ${a.remaining?.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </Box>
        )}

        {/* Currency Breakdown */}
        {currencyBreakdown.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowCurrencyBreakdown((v) => !v)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("earningsCard.currencyBreakdown")}
              </Typography>
              <IconButton size="small">
                {showCurrencyBreakdown ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </IconButton>
            </Box>
            <Collapse in={showCurrencyBreakdown}>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("earningsCard.columns.currency")}</TableCell>
                    <TableCell align="right">
                      {t("earningsCard.columns.amount")}
                    </TableCell>
                    <TableCell align="right">
                      {t("earningsCard.columns.count")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currencyBreakdown.map((c) => (
                    <TableRow key={c.royalty_currency}>
                      <TableCell>{c.royalty_currency}</TableCell>
                      <TableCell align="right">
                        {parseFloat(c.total).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">{c.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </Box>
        )}

        {/* Earnings Trend */}
        {trendData.length > 1 && (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setShowTrend((v) => !v)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("earningsCard.earningsTrend")}
              </Typography>
              <IconButton size="small">
                {showTrend ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </IconButton>
            </Box>
            <Collapse in={showTrend}>
              <Box sx={{ mt: 1, maxHeight: 200 }}>
                <ChartRenderer
                  chartType="line"
                  labels={trendLabels}
                  dataValues={trendData}
                  color="#009efd"
                  chartTitle="Earnings"
                />
              </Box>
            </Collapse>
          </Box>
        )}

        <Typography
          variant="body2"
          sx={{ mt: 3, color: "text.secondary", lineHeight: 1.6 }}
        >
          {t("earningsCard.description")}
        </Typography>
      </Paper>
    </Box>
  );
};

export default EarningsCard;
